// web/src/lib/img.js
// Cloudflare Image Transformations for display-sized images.
//
// Horse photos live in the public R2 bucket behind files.stallside.io, and the
// app was rendering the FULL upload into a 48px circle — a 1.3 MB JPEG for a
// thumbnail. Cloudflare can resize and re-encode at the edge:
//
//   https://files.stallside.io/cdn-cgi/image/<options>/<path>
//
// IMPORTANT: this requires Image Transformations to be enabled for the
// stallside.io zone (Cloudflare dashboard → Images → Transformations). Until it
// is, /cdn-cgi/image/ 404s, which would break every photo in the app. So it is
// behind VITE_IMG_TRANSFORM and defaults OFF — set it to "1" in
// web/.env.production once the zone is switched on.
//
// <SmartImg> additionally falls back to the original URL on error, so even a
// misconfiguration degrades to "large but visible" rather than a broken icon.

const ENABLED = import.meta.env.VITE_IMG_TRANSFORM === '1';

// Only rewrite images we actually serve; leave data:/blob:/third-party alone.
const FILES_HOST = 'files.stallside.io';

/**
 * A resized variant of an image URL.
 *
 * @param {string} url    original absolute URL
 * @param {number} width  intended CSS width in px — this doubles it for retina
 * @param {object} opts   { fit, quality, dpr }
 * @returns {string} transformed URL, or the original when not applicable
 */
export function imgUrl(url, width, opts = {}) {
  if (!url || typeof url !== 'string') return url;
  if (!ENABLED) return url;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url; // relative or malformed — not ours to touch
  }
  if (parsed.hostname !== FILES_HOST) return url;
  if (parsed.pathname.startsWith('/cdn-cgi/')) return url; // already transformed

  const { fit = 'cover', quality = 80, dpr = 2 } = opts;
  const opt = [
    `width=${Math.round(width * dpr)}`,
    `fit=${fit}`,
    `quality=${quality}`,
    'format=auto', // AVIF/WebP when the browser accepts it
  ].join(',');

  // Source path is relative to the same zone, minus the leading slash.
  const path = parsed.pathname.replace(/^\/+/, '');
  return `${parsed.origin}/cdn-cgi/image/${opt}/${path}`;
}

/** Whether transformations are switched on — handy for a settings readout. */
export const imgTransformEnabled = ENABLED;
